import { useId, type CSSProperties } from 'react';
import { useTheme } from '../theme/theme';
import { VehicleArt } from './Vehicle';

/**
 * The Trikuta & Chenab scene, night and day, drawn as code (after the owner's mockup scene, not copied from it):
 * the three peaks of Trikuta with snow, the far ranges, pine hills, the Chenab with moving highlights, a village
 * with a temple flag and lit windows, a boat, a lamp post, people on the bank, and the road with an auto full of
 * riders driving along it.
 *
 * 1600×900 art drawn to fill any box (`xMidYMax slice`): the sky crops first, the sides next. Everything that
 * matters sits inside x 400–1200, so a phone (≈800 units wide), a landscape phone and the desktop hero all show the
 * peaks, the river, the village and the auto. Motion is transform/opacity only and stops with reduced motion.
 */

// the road: a straight band rising to the right (dashes roll along it with a plain translate)
const ROAD_Y0 = 850;
const ROAD_RISE = 124; // over 1600 units
/** the valley (mountains → near bank) sits this much lower than it is drawn below */
const DROP = 92;
const ROAD_ANGLE = (Math.atan2(-ROAD_RISE, 1600) * 180) / Math.PI;
const roadY = (x: number) => ROAD_Y0 - (ROAD_RISE * x) / 1600;

/** A fixed, scattered star field (seeded, so it never changes between renders). */
const STARS = (() => {
  let s = 7;
  const rnd = () => ((s = (s * 16807) % 2147483647) / 2147483647);
  const out: { x: number; y: number; r: number; tw: boolean; d: number }[] = [];
  while (out.length < 70) {
    const x = rnd() * 1600;
    const y = 24 + rnd() * 420;
    // keep clear of the moon and of the TRIKUTA label, and stay above the peaks
    if (Math.hypot(x - 1080, y - 240) < 72) continue;
    if (x > 680 && x < 920 && y > 270) continue;
    if (y > 330 && x > 420 && x < 1200) continue;
    out.push({ x, y, r: 0.8 + rnd() * 1.5, tw: rnd() < 0.45, d: rnd() * 4 });
  }
  return out;
})();

const tree = (x: number, y: number, s: number) =>
  `M${x} ${y - s * 3.2}L${x + s * 0.95} ${y - s * 1.5}H${x + s * 0.45}L${x + s * 1.2} ${y}H${x - s * 1.2}L${x - s * 0.45} ${y - s * 1.5}H${x - s * 0.95}Z`;

const PINES: [number, number, number][] = [
  [60, 524, 9], [92, 520, 12], [126, 526, 8], [300, 512, 10], [332, 516, 13], [366, 512, 9],
  [418, 516, 11], [446, 510, 15], [478, 514, 10], [508, 518, 12], [540, 520, 8],
  [1040, 512, 10], [1068, 506, 14], [1098, 510, 11], [1128, 506, 15], [1160, 512, 10], [1190, 516, 12],
  [1330, 512, 11], [1362, 508, 14], [1396, 514, 9], [1500, 518, 12], [1532, 514, 9],
];

function House({ x, y, w = 28, h = 17, lit = true, d = 0 }: { x: number; y: number; w?: number; h?: number; lit?: boolean; d?: number }) {
  return (
    <g>
      <rect x={x} y={y - h} width={w} height={h} fill="var(--house)" />
      <path d={`M${x - 3} ${y - h}L${x + w / 2} ${y - h - 11}L${x + w + 3} ${y - h}Z`} fill="var(--roof)" />
      <rect x={x + w / 2 - 3} y={y - h + 5} width={6} height={6} rx={1} fill="var(--win)" className={lit ? 'zr-glim' : undefined} style={lit ? { animationDelay: `${d}s` } : undefined} />
    </g>
  );
}

export function Scene({ className, style, labels = true }: { className?: string; style?: CSSProperties; labels?: boolean }) {
  const { mode } = useTheme();
  const id = useId().replace(/:/g, '');
  const night = mode === 'dark';
  const autoX = 860;
  const autoY = roadY(autoX) + 9;
  return (
    <svg aria-hidden="true" className={className} viewBox="0 0 1600 900" preserveAspectRatio="xMidYMax slice" style={{ display: 'block', width: '100%', height: '100%', ...style }}>
      <defs>
        <linearGradient id={`${id}sky`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={night ? '#0D0D0D' : '#FFFFFF'} />
          <stop offset="0.62" stopColor={night ? '#0E1213' : '#FFFFFF'} />
          <stop offset="1" stopColor={night ? '#101718' : '#F4FBFB'} />
        </linearGradient>
        <radialGradient id={`${id}glow`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#5CE1E6" stopOpacity={night ? 0.16 : 0.12} />
          <stop offset="1" stopColor="#5CE1E6" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${id}moonglow`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0.35" stopColor="#F5F5F3" stopOpacity="0.1" />
          <stop offset="1" stopColor="#F5F5F3" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${id}beam`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#FFF6D6" stopOpacity="0.55" />
          <stop offset="1" stopColor="#FFF6D6" stopOpacity="0" />
        </linearGradient>
        <mask id={`${id}moon`}>
          <circle cx="1080" cy="240" r="30" fill="#FFFFFF" />
          <circle cx="1094" cy="228" r="25" fill="#000000" />
        </mask>
        <clipPath id={`${id}road`}>
          <path d={`M0 ${ROAD_Y0 - 27}L1600 ${ROAD_Y0 - ROAD_RISE - 27}V${ROAD_Y0 - ROAD_RISE + 29}L0 ${ROAD_Y0 + 29}Z`} />
        </clipPath>
      </defs>

      {/* sky */}
      <rect width="1600" height="900" fill={`url(#${id}sky)`} />
      {night ? (
        <g>
          {STARS.map((s, i) => (
            <circle key={i} cx={s.x} cy={s.y} r={s.r} fill="var(--skyStar)" opacity={s.tw ? 0.9 : 0.55} className={s.tw ? 'zr-twinkle' : undefined} style={s.tw ? { animationDelay: `${s.d}s` } : undefined} />
          ))}
          <circle cx="1080" cy="240" r="80" fill={`url(#${id}moonglow)`} className="zr-halo" />
          <rect x="1040" y="200" width="80" height="80" fill="#F5F5F3" mask={`url(#${id}moon)`} />
        </g>
      ) : (
        <g>
          <circle cx="1080" cy="240" r="62" fill="#5CE1E6" opacity="0.2" className="zr-halo" />
          <circle cx="1080" cy="240" r="34" fill="#5CE1E6" />
        </g>
      )}
      <g fill="none" stroke="var(--bird)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path className="zr-drift" d="M548 360q6-6 11 0q5-6 11 0M584 342q5-5 9 0q4-5 9 0" />
        <path className="zr-drift" style={{ animationDelay: '-4s' }} d="M1160 372q6-6 11 0q5-6 11 0" />
      </g>

      <g transform={`translate(0 ${DROP})`}>
      {/* far ranges and Trikuta */}
      <path d="M0 580V470L90 440L180 456L262 410L342 430L420 398L500 440L560 470L600 580Z" fill="var(--h2)" />
      <path d="M1010 580L1040 470L1120 420L1200 440L1290 398L1380 430L1470 408L1600 452V580Z" fill="var(--h2)" />
      <path d="M380 560L520 432L600 384L662 334L706 294L740 262L770 300L800 238L832 292L862 258L904 300L962 342L1040 384L1140 432L1262 560Z" fill="var(--mh2)" />
      <path d="M740 262L752 280L744 277L738 284L731 276L724 280Z M800 238L816 266L806 261L799 270L791 261L782 266Z M862 258L874 277L866 273L860 280L853 273L846 277Z" fill="var(--snow)" />
      <path d="M560 470Q660 430 760 452Q860 474 960 446Q1060 420 1120 452L1120 580H560Z" fill="var(--mh1)" />
      {labels && (
        <text x="800" y="214" textAnchor="middle" fontSize="13" fontWeight="700" letterSpacing="4.6" fill="var(--ink2)" style={{ fontFamily: 'Satoshi, system-ui, sans-serif' }}>
          TRIKUTA
        </text>
      )}

      {/* the glow over the valley at night */}
      {night && <ellipse cx="820" cy="640" rx="560" ry="170" fill={`url(#${id}glow)`} className="zr-breathe" />}

      {/* pine hills */}
      <path d="M0 610V522Q120 494 240 508Q360 522 470 504Q560 490 650 510Q740 528 830 516Q930 502 1030 506Q1130 510 1230 500Q1360 488 1470 506Q1540 516 1600 510V610Z" fill="var(--h1)" />
      <g fill="var(--tree)">
        {PINES.filter((_, i) => i % 2 === 0).map(([x, y, s]) => (
          <path key={x} d={tree(x, y, s)} />
        ))}
      </g>
      <g fill="var(--tree2)">
        {PINES.filter((_, i) => i % 2 === 1).map(([x, y, s]) => (
          <path key={x} d={tree(x, y, s)} />
        ))}
      </g>

      {/* the village on the far bank */}
      <g>
        <House x={612} y={584} d={0.3} />
        <House x={648} y={586} w={24} h={14} lit={night} d={1.4} />
        <g>
          <rect x="694" y="566" width="30" height="20" fill="var(--house)" />
          <path d="M698 566L709 538L720 566Z" fill="var(--roof)" />
          <path d="M709 538V512" stroke="var(--fig)" strokeWidth="2" />
          <path className="zr-flag" d="M710 512L728 517L710 523Z" fill="#5CE1E6" />
          <rect x="705" y="574" width="8" height="12" rx="4" fill="var(--win)" opacity="0.85" />
        </g>
        <House x={736} y={586} d={2.1} />
        <House x={772} y={584} w={22} h={13} lit={false} />
        <House x={1006} y={584} w={26} h={15} d={0.9} />
        <House x={1040} y={586} lit={night} d={2.6} />
      </g>

      {/* the Chenab */}
      <path d="M0 588Q200 582 400 590Q600 598 800 590Q1000 582 1200 590Q1400 598 1600 588V624Q1400 632 1200 626Q1000 620 800 628Q600 636 400 628Q200 620 0 626Z" fill="var(--river)" />
      <g fill="var(--riverHi)">
        {[
          [430, 604, 70, 0],
          [560, 614, 46, 1.2],
          [760, 606, 90, 2.4],
          [930, 616, 54, 0.6],
          [1080, 604, 72, 1.8],
          [250, 612, 56, 3],
          [1300, 610, 64, 2],
        ].map(([x, y, w, d]) => (
          <rect key={x} x={x} y={y} width={w} height="2.4" rx="1.2" className="zr-shimmer" style={{ animationDelay: `${-d}s` }} />
        ))}
      </g>
      {labels && (
        <text x="452" y="574" fontSize="12" fontWeight="700" letterSpacing="4.2" fill="var(--ink2)" style={{ fontFamily: 'Satoshi, system-ui, sans-serif' }}>
          CHENAB
        </text>
      )}
      {/* a boat on the river */}
      <g className="zr-rock">
        <path d="M612 606H660L652 616H620Z" fill="var(--fig)" />
        <circle cx="636" cy="594" r="3.6" fill="var(--fig)" />
        <path d="M636 598V606M636 600L650 612" stroke="var(--fig)" strokeWidth="2.4" strokeLinecap="round" />
      </g>

      {/* the near bank: lamp, people */}
      <rect x="0" y="624" width="1600" height="400" fill="var(--bg)" />
      <g>
        <path d="M470 680V640" stroke="var(--fig)" strokeWidth="3" strokeLinecap="round" />
        <circle cx="470" cy="636" r="6" fill="var(--win)" className="zr-glim" />
        {night && <circle cx="470" cy="636" r="18" fill="#5CE1E6" opacity="0.12" />}
        <circle cx="520" cy="650" r="5" fill="var(--fig)" />
        <rect x="514" y="656" width="12" height="22" rx="5" fill="var(--fig)" />
        <circle cx="538" cy="660" r="3.6" fill="var(--fig)" />
        <rect x="534" y="664" width="8" height="14" rx="3.5" fill="var(--fig)" />
        <circle cx="1150" cy="642" r="4.6" fill="var(--fig)" />
        <rect x="1145" y="647" width="10" height="20" rx="4.5" fill="var(--fig)" />
      </g>

      </g>

      {/* the road, its dashes rolling towards the viewer's left as the auto drives right */}
      <path d={`M0 ${ROAD_Y0 - 27}L1600 ${ROAD_Y0 - ROAD_RISE - 27}V${ROAD_Y0 - ROAD_RISE + 29}L0 ${ROAD_Y0 + 29}Z`} fill="var(--road)" />
      <path d={`M0 ${ROAD_Y0 - 27}L1600 ${ROAD_Y0 - ROAD_RISE - 27}`} stroke="var(--roadDash)" strokeWidth="2" opacity="0.35" />
      <g clipPath={`url(#${id}road)`}>
        <g transform={`translate(0 ${ROAD_Y0}) rotate(${ROAD_ANGLE})`}>
          <g className="zr-roll" fill="var(--roadDash)">
            {Array.from({ length: 21 }, (_, i) => (
              <rect key={i} x={i * 90 - 90} y="-2.5" width="44" height="5" rx="2.5" />
            ))}
          </g>
        </g>
      </g>

      {/* the auto with its riders */}
      <g transform={`translate(${autoX} ${autoY}) rotate(${ROAD_ANGLE}) scale(1.6) translate(-60 -66)`}>
        {night && <path d="M108 50L190 36V70Z" fill={`url(#${id}beam)`} className="zr-breathe" />}
        <g className="zr-bob">
          <VehicleArt type="AUTO" width={120} spin shadow={false} />
        </g>
      </g>
    </svg>
  );
}
